import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "next-i18next";
import { storyblokEditable } from "@storyblok/react";
import { analytics } from "@lib/trackers/analytics";
import { useCurrentUser } from "@hooks/use-current-user";
import Link from "@components/core/Link";
import { getListingPageInformation } from "@utils/listingPage";
import { getProducts } from "@utils/call-api/products-call";
import { userState } from "@utils/constants/user";
import ProductSliderV2Gallery from "./ProductSliderV2Gallery";

function ProductSliderV2({ blok, t }) {
  const user = useCurrentUser();

  const [products, setProducts] = useState([]);
  const [listingPage, setListingPage] = useState({});

  const getUserGroup = useCallback(() => {
    if (!user) return null;
    if (user.c1_dedicated) return "c1";
    if (user.user_group.includes("trying")) return "trying";

    return "confirmed";
  }, [user]);

  const listingPageUrl = () => `/${listingPage?.hub?.slug}/${listingPage?.slug}`;

  const productsListViewEvent = useCallback(
    (productsData) => {
      const productsUids = productsData.map((product) => product.objectID);

      if (!productsUids || user?.state !== userState?.subscriber) return;

      analytics.track("products_list_view", {
        source: "home",
        hub_or_listing_page_slug: blok.listing_page_slug,
        product_uids: productsUids,
        filters: {},
        user_group: getUserGroup(),
        origin: "next_js",
        ab_test: {},
      });
    },
    [blok.listing_page_slug, getUserGroup],
  );

  useEffect(() => {
    async function fetchListingPageAndProducts() {
      if (!blok?.listing_page_slug) return;

      const listingPageRes = await getListingPageInformation(blok.listing_page_slug);
      const productsRes = await getProducts(
        listingPageRes?.hub?.context,
        listingPageRes?.search_query,
      );

      setProducts(productsRes?.data || []);
      setListingPage(listingPageRes);

      const productsData = productsRes?.data;

      if (productsData?.length > 4) {
        productsListViewEvent(productsData);
      }
    }

    fetchListingPageAndProducts();
  }, [blok.listing_page_slug]);

  if (!blok || !products?.length || products?.length <= 4) return null;

  return (
    <section
      key={blok._uid}
      {...storyblokEditable(blok)}
      className="storyblok-home-section"
    >
      <div className="product-slider-v2-container">
        <div className="text-center px-3">
          <h4>{blok.title}</h4>
          <p className="font-size-sm text-gray">{blok.subtitle}</p>
        </div>
        <Link
          href={listingPageUrl()}
          className="product-slider-v2-header-link"
        >
          {t("product:see_more")}
        </Link>
        <ProductSliderV2Gallery
          products={products}
          listingPage={listingPage}
        />
      </div>
    </section>
  );
}

export default withTranslation("product")(ProductSliderV2);
